import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MobileLayout from '../../components/MobileLayout';
import { useVoices } from '../../../hooks/useApi';
import './MobileSelectVoice.css';

const IconPlus   = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IconCheck  = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
const IconSearch = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const IconPlay   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>;
const IconPause  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>;

const WAVE_HEIGHTS = [5, 10, 7, 14, 9, 12, 6, 16, 8, 10, 5];
const GENDER_LABEL = { male: '男', female: '女' };

export default function MobileSelectVoice() {
  const navigate = useNavigate();
  const location = useLocation();

  const [search, setSearch]     = useState('');
  const [page, setPage]         = useState(1);
  const [selected, setSelected] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  const returnTo = location.state?.from || '/m/generate';

  const { voices, loading, error, totalPages, refetch } = useVoices('my', page, search);

  // Pre-select current voice
  useEffect(() => {
    try {
      const saved = localStorage.getItem('selectedVoice');
      if (saved) setSelected(JSON.parse(saved));
    } catch {}
  }, []);

  // Clean up audio on unmount
  useEffect(() => () => { audioRef.current?.pause(); }, []);

  const handleSelect = (voice) => {
    localStorage.setItem('selectedVoice', JSON.stringify(voice));
    audioRef.current?.pause();
    navigate(returnTo, { replace: true });
  };

  const handleCreateNew = () => {
    navigate('/m/create-voice', { state: { from: location.pathname, returnTo } });
  };

  const handlePlayToggle = (e, voice) => {
    e.stopPropagation();
    if (!voice.sampleUrl) return;

    if (playingId === voice.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
    }
    const audio = new Audio(voice.sampleUrl);
    audioRef.current = audio;
    audio.play().catch(() => {});
    setPlayingId(voice.id);
    audio.onended = () => setPlayingId(null);
  };

  return (
    <MobileLayout title="选择声音" showBack>
      <div className="msv-root">
        {/* Search */}
        <div className="msv-search-wrap">
          <span className="msv-search-icon"><IconSearch /></span>
          <input
            className="msv-search"
            type="search"
            placeholder="搜索声音名称..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>

        <div className="msv-list">
          {/* Create new */}
          <div className="msv-create-row" onClick={handleCreateNew}>
            <div className="msv-create-circle"><IconPlus /></div>
            <div className="msv-create-info">
              <div className="msv-create-label">克隆新声音</div>
              <div className="msv-create-hint">录音或上传音频，克隆专属声音</div>
            </div>
          </div>

          {loading && page === 1 ? (
            <div className="msv-loading"><div className="msv-spinner" /></div>
          ) : error ? (
            <div className="msv-error">{error} <button onClick={() => refetch()}>重试</button></div>
          ) : voices.length === 0 ? (
            <div className="msv-empty">
              {search ? '没有匹配的声音' : '还没有克隆声音，创建一个吧'}
            </div>
          ) : (
            voices.map((voice) => {
              const isSelected = selected?.id === voice.id;
              const isPlaying  = playingId === voice.id;
              const gender     = voice.gender || 'unknown';
              return (
                <div
                  key={voice.id}
                  className={`msv-card${isSelected ? ' selected' : ''}`}
                  onClick={() => handleSelect(voice)}
                >
                  <div className={`msv-wave-wrap${gender === 'male' ? ' male' : ''}`}>
                    <div className="msv-wave">
                      {WAVE_HEIGHTS.map((h, i) => (
                        <div
                          key={i}
                          className="msv-wave-bar"
                          style={{ height: `${h}px` }}
                        />
                      ))}
                    </div>
                    {GENDER_LABEL[gender] && (
                      <span className="msv-gender">{GENDER_LABEL[gender]}</span>
                    )}
                  </div>

                  <div className="msv-card-info">
                    <div className="msv-card-name">{voice.name}</div>
                    <div className="msv-card-sub">{voice.language || '中文'}</div>
                  </div>

                  {voice.sampleUrl && (
                    <button
                      className={`msv-play-btn${isPlaying ? ' playing' : ''}`}
                      onClick={(e) => handlePlayToggle(e, voice)}
                      aria-label={isPlaying ? '暂停' : '试听'}
                    >
                      {isPlaying ? <IconPause /> : <IconPlay />}
                    </button>
                  )}

                  {isSelected && (
                    <div className="msv-check"><IconCheck /></div>
                  )}
                </div>
              );
            })
          )}

          {/* Load more */}
          {totalPages > page && (
            <div className="msv-load-more">
              <button
                className="msv-load-more-btn"
                onClick={() => setPage((p) => p + 1)}
                disabled={loading}
              >
                {loading ? '加载中...' : '加载更多'}
              </button>
            </div>
          )}
        </div>
      </div>
    </MobileLayout>
  );
}
